import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import theme, { buttonClasses, textClasses } from "../../../static/styles/theme";
import axios from "../../../services/Api";

const IMAGE_REGEX = /\.(jpg|jpeg|png|gif|bmp|tiff)$/i;

/**
 * Hook to fetch a thumbnail URL for a given file key from S3.
 * Only fetches for image files; non-image files skip the API call.
 *
 * @param {string} fileKey - The S3 file key
 * @returns {{ thumbnailUrl: string|null, thumbnailLoading: boolean }}
 */
export function useFileThumbnail(fileKey) {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [thumbnailLoading, setThumbnailLoading] = useState(false);

    useEffect(() => {
        if (!fileKey || !IMAGE_REGEX.test(fileKey)) {
            setThumbnailUrl(null);
            return;
        }
        let cancelled = false;
        setThumbnailLoading(true);
        axios
            .post(`/inventory/getS3FileForItem`, { fileKey, thumbnail: true })
            .then(({ data: res }) => {
                if (!cancelled) setThumbnailUrl(res.fileUrl);
            })
            .catch(() => {
                if (!cancelled) setThumbnailUrl(null);
            })
            .finally(() => {
                if (!cancelled) setThumbnailLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fileKey]);

    return { thumbnailUrl, thumbnailLoading };
}

/**
 * GenericFileThumbnail - Displays a thumbnail for image files or a "View File" link for other file types.
 *
 * @param {string}   fileKey    - The S3 file key / file path
 * @param {function} onClick    - Callback when the thumbnail or "View File" link is clicked, receives (fileKey)
 * @param {boolean}  [showThumbnail=true] - Whether to fetch and show image thumbnails (set false for legacy "View File" only behavior)
 */
export default function GenericFileThumbnail({ fileKey, onClick, showThumbnail = true }) {
    const { thumbnailUrl, thumbnailLoading } = useFileThumbnail(showThumbnail ? fileKey : null);

    const isImage = IMAGE_REGEX.test(fileKey);

    if (!fileKey) {
        return <Typography sx={{ ...textClasses.t12n }}>No Img</Typography>;
    }

    if (thumbnailLoading) {
        return <Typography sx={{ ...textClasses.t12n }}>Loading...</Typography>;
    }

    if (showThumbnail && isImage && thumbnailUrl) {
        return (
            <img
                src={thumbnailUrl}
                alt="thumbnail"
                style={{
                    width: 60,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: `1px solid ${theme.themeOrange}`,
                    cursor: "pointer",
                }}
                onClick={() => onClick?.(fileKey)}
            />
        );
    }

    return (
        <div onClick={() => onClick?.(fileKey)} style={{ cursor: "pointer" }}>
            <span
                style={{
                    ...buttonClasses.viewFile,
                    cursor: "pointer",
                }}
            >
                View File
            </span>
        </div>
    );
}
